import { type FunctionComponent, useEffect, useState } from 'react';

import { Button } from '@dr.pogodin/react-utils';
import { captureException } from '@sentry/react';

import Throbber from 'components/Throbber';

import { useUserAssets } from 'hooks';
import { useGallery } from 'hooks/artists/useGallery';
import { useProfileHeroImage } from 'hooks/artists/useProfileHeroImage';

import {
  deleteAsset,
  saveGallery,
  setProfileHeroImage,
  uploadDocument,
} from 'services';

import {
  type ArtistT,
  type AssetT,
  type ImageAssetT,
  getId,
  useGlobalState,
} from 'utils';

import IconInfo from 'assets/images/icon-info-24px.svg';

import { GalleryPhotos } from './GalleryPhotos';
import { Library } from './Library';
import { ProfilePhoto } from './ProfilePhoto';
import { UsePhotoModal } from './UsePhotoModal';

import './style.scss';

type LocalGalleryItemT = File | ImageAssetT | null;

const MAX_GALLERY_SIZE = 8;

function equalGalleries(
  gallery: ImageAssetT[],
  local: LocalGalleryItemT[],
): boolean {
  for (let i = 0; i < Math.max(gallery.length, local.length); ++i) {
    const loc = local[i];

    if (loc) {
      if (loc instanceof File || loc.id !== gallery[i]?.id) return false;
    } else if (gallery[i]) return false;
  }

  return true;
}

function preventDefault(e: Event) {
  e.preventDefault();
}

function normalizeGallery(gallery: ImageAssetT[] | null): LocalGalleryItemT[] {
  if (!gallery) return new Array<null>(MAX_GALLERY_SIZE);
  if (gallery.length >= MAX_GALLERY_SIZE) return gallery;

  const res: LocalGalleryItemT[] = gallery.slice();
  while (res.length < MAX_GALLERY_SIZE) res.push(null);
  return res;
}

type PropsT = {
  artist: ArtistT;
};

export const ArtistMediaManager: FunctionComponent<PropsT> = ({
  artist,
}) => {
  const [userId] = useGlobalState('user.id');

  const artistId = getId(artist);
  const hero = useProfileHeroImage(artistId);
  const gallery = useGallery(artistId);

  useEffect(() => {
    window.addEventListener('dragover', preventDefault);
    window.addEventListener('drop', preventDefault);
    return () => {
      window.removeEventListener('dragover', preventDefault);
      window.removeEventListener('drop', preventDefault);
    };
  }, []);

  const [
    localHero,
    setLocalHero,
  ] = useState<File | ImageAssetT | null>(hero.data);

  const [lastHeroData, setLastHeroData] = useState(hero.data);
  if (hero.data !== lastHeroData) {
    setLastHeroData(hero.data);
    setLocalHero(hero.data);
  }

  const [
    localGallery,
    setLocalGallery,
  ] = useState(() => normalizeGallery(gallery.data));

  const [lastGalleryData, setLastGalleryData] = useState(gallery.data);
  if (gallery.data !== lastGalleryData) {
    const next = normalizeGallery(gallery.data);
    setLastGalleryData(gallery.data);
    setLocalGallery(next);
  }

  let numFreeGallerySlots = 0;
  for (const item of localGallery) {
    if (!item) ++numFreeGallerySlots;
  }

  const assets = useUserAssets(artist.user.id);

  const [
    assetsToRemove,
    setAssetsToRemove,
  ] = useState<Record<string, true>>({});

  const [localFiles, setLocalFiles] = useState<File[]>([]);

  const visibleAssets: AssetT[] = assets.data?.filter(
    (item) => !assetsToRemove[item.id],
  ) ?? [];

  const [usePhotoModal, setUsePhotoModal] = useState<File | ImageAssetT>();

  const canSave = localFiles.length
    || Object.keys(assetsToRemove).length
    || localHero instanceof File
    || (!!localHero && localHero.id !== hero.data?.id)
    || !equalGalleries(gallery.data ?? [], localGallery);

  const [busy, setBusy] = useState<number | undefined>(undefined);

  return (
    <div styleName="container">
      {
        visibleAssets.length && userId === artist.user.id ? (
          <div styleName="infoBubble">
            <IconInfo />
            You have files in your library from onboarding. Choose which
            to feature as your profile photo and gallery, or leave them
            stored privately.
          </div>
        ) : null
      }
      <ProfilePhoto
        image={localHero}
        setImage={(image) => {
          if (image instanceof File && !localFiles.includes(image)) {
            setLocalFiles([...localFiles, image]);
          }
          setLocalHero(image);
        }}
      />
      <div styleName="separator" />
      <GalleryPhotos
        images={localGallery}
        setImages={(images) => {
          const local = new Set(localFiles);
          for (const item of images) {
            if (item instanceof File && !local.has(item)) {
              setLocalFiles((prev) => [...prev, item]);
              local.add(item);
            }
          }
          setLocalGallery(images);
        }}
      />
      <div styleName="separator" />
      {/*
      <div>INTRO VIDEO</div>
      <div styleName="separator" />
      */}
      <Library
        assets={visibleAssets}
        localFiles={localFiles}
        onUse={(item) => {
          if (!(item instanceof File) && item.kind !== 'IMAGE') {
            throw Error('Unexpected item kind');
          }
          setUsePhotoModal(item);
        }}
        removeAsset={(asset) => {
          let newGallery: typeof localGallery | undefined;
          for (let i = 0; i < localGallery.length; ++i) {
            const item = localGallery[i];
            if (item && !(item instanceof File) && item.id === asset.id) {
              newGallery ??= localGallery.slice();
              newGallery[i] = null;
            }
          }
          if (newGallery) setLocalGallery(newGallery);

          setAssetsToRemove({
            ...assetsToRemove,
            [asset.id]: true,
          });
        }}
        setLocalFiles={(files) => {
          const set = new Set(files);

          let newGallery: typeof localGallery | undefined;
          for (let i = 0; i < localGallery.length; ++i) {
            const item = localGallery[i];
            if (item instanceof File && !set.has(item)) {
              newGallery ??= localGallery.slice();
              newGallery[i] = null;
            }
          }
          if (newGallery) setLocalGallery(newGallery);

          setLocalFiles(files);
        }}
      />
      <div styleName="separator" />
      <div styleName="buttons">
        <Button
          disabled={!canSave}
          onClick={() => {
            void (async () => {
              let failedToRemove = false;
              try {
                setBusy(0);

                // Uploads local files.

                let totalSize = 0;
                let uploadedSize = 0;
                for (const file of localFiles) totalSize += file.size;

                const fileMap = new Map<File, string>();
                for (const file of localFiles) {
                  // TODO: Wire up to the document upload progress events,
                  // to update the progress even more gradually.
                  const a = await uploadDocument({
                    document: file,
                    practitionerId: artistId,
                  });
                  fileMap.set(file, a.id);
                  uploadedSize += file.size;
                  setBusy(Math.floor(100 * uploadedSize / totalSize));
                }
                setLocalFiles([]);

                // Saves the hero image.
                if (localHero instanceof File) {
                  const id = fileMap.get(localHero);
                  if (!id) throw Error('Internal error');
                  hero.set(await setProfileHeroImage(artistId, id));
                } else if (localHero && localHero.id !== hero.data?.id) {
                  hero.set(await setProfileHeroImage(artistId, localHero.id));
                }

                // Saves the galery.
                const g: string[] = [];
                for (const item of localGallery) {
                  if (item) {
                    if (item instanceof File) {
                      const id = fileMap.get(item);
                      if (!id) throw Error('Internal error');
                      g.push(id);
                    } else {
                      g.push(item.id);
                    }
                  }
                }
                gallery.set(await saveGallery(artistId, g));

                // Removes assets.
                for (const asset of Object.keys(assetsToRemove)) {
                  try {
                    await deleteAsset(asset);
                  } catch (e) {
                    failedToRemove = true;
                    captureException(e);
                  }
                }
                setAssetsToRemove({});

                // Reload stuff.
                await assets.reload();
              } finally {
                setBusy(undefined);
              }

              if (failedToRemove) {
                // eslint-disable-next-line no-alert
                alert('Some assets could not be removed');
              }
            })();
          }}
        >
          Save Changes
        </Button>
      </div>
      {usePhotoModal ? (
        <UsePhotoModal
          addAsHero={() => {
            setLocalHero(usePhotoModal);
            setUsePhotoModal(undefined);
          }}
          addToGallery={() => {
            let neu: typeof localGallery | undefined;
            for (let i = 0; i < localGallery.length; ++i) {
              if (!localGallery[i]) {
                neu = localGallery.slice();
                neu[i] = usePhotoModal;
                break;
              }
            }
            if (!neu) throw Error('Internal error');
            setUsePhotoModal(undefined);
            setLocalGallery(neu);
          }}
          numFreeGallerySlots={numFreeGallerySlots}
          numGallerySlots={localGallery.length}
          onClose={() => {
            setUsePhotoModal(undefined);
          }}
          photo={usePhotoModal}
        />
      ) : null}
      {busy === undefined ? null
        : <div styleName="busy"><Throbber progress={busy} /></div>}
    </div>
  );
};
